import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

/* eslint-disable react-refresh/only-export-components -- standard context pattern: provider, hook, and context co-located */
import { logger } from '@/lib/logger';

type Theme = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  isDark: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setThemeState] = useState<Theme>(() => {
    // Get theme from localStorage or default to system
    try {
      return (localStorage.getItem('theme') as Theme) || 'system';
    } catch (err) {
      logger.warn('Failed to load theme from localStorage', {
        error: err,
        component: 'ThemeContext',
      });
      return 'system';
    }
  });

  const [isDark, setIsDark] = useState(false);

  const syncTheme = useCallback(() => {
    const prefersDark = window.matchMedia(
      '(prefers-color-scheme: dark)'
    ).matches;
    const shouldBeDark =
      theme === 'dark' || (theme === 'system' && prefersDark);

    setIsDark(shouldBeDark);

    // Apply theme to document
    // Note: We add both 'dark' (for design-system.css) and 'dark-mode' (for theme.css/Untitled UI)
    if (shouldBeDark) {
      document.documentElement.classList.add('dark', 'dark-mode');
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark', 'dark-mode');
      document.documentElement.setAttribute('data-theme', 'light');
    }
  }, [theme]);

  const setTheme = useCallback(
    (newTheme: Theme) => {
      setThemeState(newTheme);
      try {
        localStorage.setItem('theme', newTheme);
      } catch (err) {
        logger.warn('Failed to persist theme preference', {
          error: err,
          component: 'ThemeContext',
        });
      }
      syncTheme();
    },
    [syncTheme]
  );

  useEffect(() => {
    syncTheme();

    // Watch for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (theme === 'system') {
        syncTheme();
      }
    };

    mediaQuery.addEventListener('change', handleChange);

    // Watch for localStorage changes from other tabs
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'theme') {
        const newTheme = (e.newValue as Theme) || 'system';
        setThemeState(newTheme);
        syncTheme();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [theme, syncTheme]);

  const value: ThemeContextType = useMemo(
    () => ({
      theme,
      setTheme,
      isDark,
    }),
    [theme, setTheme, isDark]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};
